/**
 * AWS WAF CAPTCHA integration (spec §NFR-6 — spam/abuse gate on the public "Report a Concern"
 * submit). The WebACL (infra/modules/api/security.tf, rule `captcha-public-submit`) applies a
 * CAPTCHA action to `POST /api/public/requests`. A request WITHOUT a valid, unexpired
 * `aws-waf-token` gets AWS WAF's CAPTCHA challenge (HTTP 405 + challenge body) instead of hitting
 * the API; a request WITH a fresh token passes straight through. The token is minted by the AWS WAF
 * JS SDK (loaded from the account integration URL as `jsapi.js`) via `renderCaptcha`.
 *
 * ## Inline-before-submit (2026-08-16) — the puzzle renders ON MOUNT, not on submit
 *
 * We used to render the CAPTCHA lazily, ON SUBMIT, into an otherwise-empty "Spam check" box. That
 * left the box looking broken (only explanatory text, no puzzle) and surprised residents with a
 * puzzle *after* they clicked. Now the shared `useCaptcha` hook calls `renderCaptchaInline` when the
 * field mounts, so the resident solves the puzzle inline as part of the form and THEN clicks submit
 * — the pattern every other site uses. Under WAF immunity `renderCaptcha` resolves instantly with no
 * visible puzzle → the field shows a clean "verified" state.
 *
 * This module wraps the SDK globals (`AwsWafIntegration`, `AwsWafCaptcha`) with:
 *   - a lazy loader that polls for the async globals (`jsapi.js` sets
 *     `window.AwsWafCaptcha` / `window.AwsWafIntegration` ~300ms AFTER the script's `load` event, so
 *     a single check rejects prematurely — we poll);
 *   - `renderCaptchaInline()` — renders the widget into a container and, on solve, stashes the solved
 *     token (with a freshness timestamp) for header delivery. Called on mount by the shared hook.
 *   - `ensureCaptchaToken()` — the SUBMIT-TIME SAFETY NET. It is a fast no-op when a fresh
 *     inline-solved token already exists; otherwise (token missing/expired) it re-renders the puzzle
 *     and awaits a fresh solve before the POST. Only a token minted by `renderCaptcha` satisfies the
 *     CAPTCHA *action* — a silent `getToken()` token does NOT (that mismatch was the root cause of
 *     the prod 405s), so this never shortcuts on `getToken`.
 *
 * ## Cross-origin embed: the token MUST ride as a HEADER, not (only) the cookie (PR #206)
 *
 * The AWS WAF SDK stores the solved token as an `aws-waf-token` cookie scoped to the token domain
 * (`towndesk.us`). A same-origin page's `fetch` sends that cookie automatically — so the standalone
 * SPA works. But the embed (ADR-0004) frames `linnvalley-ks.towndesk.us` inside a DIFFERENT parent
 * site (e.g. linnvalley.gov). Modern browsers **partition cookies/storage by the top-level site** in
 * a cross-site iframe (CHIPS / storage partitioning), so the `aws-waf-token` cookie written inside
 * the partitioned frame is NOT attached to the subsequent gated `fetch` — the POST reaches WAF with
 * no token, WAF returns its 405 CAPTCHA interstitial, and the write never lands (confirmed: 0
 * subscriber rows on prod). AWS's documented fix is to read the token via
 * `AwsWafIntegration.getToken()` and send it as the `x-aws-waf-token` HEADER — WAF reads the token
 * from that header IN ADDITION to the cookie. So after the puzzle solves, we grab the token and stash
 * it here; `apiFetch` attaches it as `x-aws-waf-token` on the very next request and then clears it.
 * This is cookie-independent and fixes ALL captcha-gated public POSTs (subscribe, report, ask,
 * records, permit, resident comment/survey) in the embed; standalone unaffected. DO NOT regress this.
 */
import { CAPTCHA_ENABLED, WAF_CAPTCHA } from "./config.js";

// ---- SDK globals (provided by jsapi.js at runtime) -------------------------

interface AwsWafIntegrationApi {
  /** Resolves with a valid WAF token, running a silent challenge if needed. */
  getToken(): Promise<string>;
  /** Fetch wrapper that attaches the token as the `x-aws-waf-token` header. */
  fetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response>;
}

interface RenderCaptchaConfig {
  apiKey: string;
  onSuccess: (wafToken: string) => void;
  onError: (error: unknown) => void;
  onLoad?: () => void;
  dynamicWidth?: boolean;
  skipTitle?: boolean;
  defaultLocale?: string;
}

interface AwsWafCaptchaApi {
  renderCaptcha(container: HTMLElement, config: RenderCaptchaConfig): void;
}

declare global {
  interface Window {
    AwsWafIntegration?: AwsWafIntegrationApi;
    AwsWafCaptcha?: AwsWafCaptchaApi;
  }
}

// ---- SDK loading -----------------------------------------------------------

/** Poll interval + max attempts while waiting for the async SDK globals (~6s total). */
const SDK_POLL_INTERVAL_MS = 50;
const SDK_POLL_MAX_TRIES = 120;

let sdkLoad: Promise<void> | null = null;

/** Lazily inject the AWS WAF integration `jsapi.js`. Idempotent; resolves when the SDK is ready. */
function loadSdk(): Promise<void> {
  if (window.AwsWafCaptcha && window.AwsWafIntegration) return Promise.resolve();
  if (sdkLoad) return sdkLoad;

  sdkLoad = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>("script[data-waf-captcha]");
    if (existing && window.AwsWafCaptcha && window.AwsWafIntegration) {
      resolve();
      return;
    }
    const src = `${WAF_CAPTCHA.integrationUrl.replace(/\/$/, "")}/jsapi.js`;
    const script = existing ?? document.createElement("script");
    script.src = src;
    script.defer = true;
    script.dataset.wafCaptcha = "true";

    // jsapi.js sets window.AwsWafCaptcha/AwsWafIntegration asynchronously — observed ~300ms AFTER
    // its `load` event fires. Poll for the globals rather than checking once, so we don't reject
    // prematurely while the SDK is still finishing its own async init.
    const onReady = () => {
      let tries = 0;
      const poll = () => {
        if (window.AwsWafCaptcha && window.AwsWafIntegration) {
          resolve();
          return;
        }
        tries += 1;
        if (tries >= SDK_POLL_MAX_TRIES) {
          reject(new Error("AWS WAF CAPTCHA SDK loaded but globals are missing."));
          return;
        }
        setTimeout(poll, SDK_POLL_INTERVAL_MS);
      };
      poll();
    };
    script.addEventListener("load", onReady, { once: true });
    script.addEventListener("error", () => reject(new Error("Failed to load the CAPTCHA SDK.")), {
      once: true,
    });
    if (!existing) document.head.appendChild(script);
    // If the deferred tag already finished loading before we attached listeners, start polling now.
    if (existing && document.readyState !== "loading") onReady();
  });
  return sdkLoad;
}

// ---- Solved-token state (freshness + header delivery) ----------------------

/**
 * WAF CAPTCHA tokens are short-lived. AWS's default token immunity is ~300s; we treat a solved token
 * as usable for a conservative window under that so we never POST with a token WAF has already
 * expired (which would 405). Past this, the submit safety net re-challenges rather than trusting a
 * stale token.
 */
const TOKEN_FRESH_MS = 4 * 60 * 1000; // 4 minutes — comfortably inside WAF's ~5min immunity.

/**
 * The freshly-minted WAF token, stashed after a puzzle solves (inline on mount, or a submit-time
 * re-challenge). Two consumers:
 *   - `takePendingCaptchaToken()` — single-use header delivery for the ONE gated request that
 *     immediately follows (the cross-origin fix, PR #206);
 *   - `hasFreshCaptchaToken()` — lets the submit path skip the re-challenge when the inline solve is
 *     still fresh (the fast no-op path).
 * `solvedAt` is the mint time, used for the freshness check. Null when there's no live WAF or the
 * SDK couldn't return one (we then fall back to the same-origin cookie, all the standalone path needs).
 */
let solvedToken: string | null = null;
let solvedAt = 0;

/** True when a solved token exists and is still within the freshness window. */
export function hasFreshCaptchaToken(): boolean {
  return solvedToken !== null && Date.now() - solvedAt < TOKEN_FRESH_MS;
}

/**
 * Consume the pending WAF token for header delivery. Returns it once, then clears it — so it rides
 * as the `x-aws-waf-token` header on exactly the next gated POST and never leaks onto a later
 * request. `apiFetch` calls this on every request; a null result just means "no header to attach"
 * (the standalone same-origin cookie path is untouched). Clearing it also drops freshness, so a
 * follow-up submit re-challenges rather than reusing a spent token.
 */
export function takePendingCaptchaToken(): string | null {
  const token = solvedToken;
  solvedToken = null;
  solvedAt = 0;
  return token;
}

/**
 * After a puzzle resolves, read the solved token via `AwsWafIntegration.getToken()` (it returns the
 * already-minted token immediately) and stash it with a fresh timestamp for header delivery. Best-
 * effort: on any failure we leave the token null and fall back to the same-origin cookie (standalone
 * path), rather than block a submit on the header optimization.
 */
async function stashSolvedToken(): Promise<void> {
  try {
    const token = await window.AwsWafIntegration?.getToken();
    solvedToken = token ?? null;
    solvedAt = token ? Date.now() : 0;
  } catch {
    solvedToken = null;
    solvedAt = 0;
  }
}

// ---- Rendering -------------------------------------------------------------

/** Outcome of an inline render, so callers can distinguish "solved a puzzle" from "immune". */
export interface CaptchaRenderResult {
  /** True when WAF immunity made `renderCaptcha` resolve instantly with no visible puzzle. */
  immune: boolean;
}

/**
 * Render the CAPTCHA widget into `container` and resolve once it's satisfied. Called ON MOUNT by the
 * shared `useCaptcha` hook (inline-before-submit) and again by the submit safety net when the token
 * has expired. ALWAYS uses `renderCaptcha` — the public-submit WAF rule is a CAPTCHA action, so only
 * a `renderCaptcha` token satisfies it. Under WAF immunity `renderCaptcha` fires `onSuccess`
 * synchronously with no visible puzzle; otherwise it resolves once the resident solves it (the
 * widget ships its own audio alternative). On solve, stashes the token for header delivery.
 *
 * No-op (resolves `{ immune: true }`) when the live WAF is disabled (local/mock/test) — the fallback
 * checkbox in CaptchaField gates instead. Rejects if the SDK fails to load or the widget errors.
 */
export async function renderCaptchaInline(container: HTMLElement): Promise<CaptchaRenderResult> {
  if (!CAPTCHA_ENABLED) return { immune: true };

  await loadSdk();

  let sawImmunity = true;
  await new Promise<void>((resolve, reject) => {
    const captcha = window.AwsWafCaptcha;
    if (!captcha) {
      reject(new Error("CAPTCHA SDK unavailable."));
      return;
    }
    captcha.renderCaptcha(container, {
      apiKey: WAF_CAPTCHA.apiKey,
      dynamicWidth: true,
      // If the widget ever paints a puzzle, this fires — so onSuccess without an onLoad paint means
      // WAF immunity resolved it silently.
      onLoad: () => {
        sawImmunity = false;
      },
      onSuccess: () => resolve(),
      onError: (err) => reject(err instanceof Error ? err : new Error("CAPTCHA failed.")),
    });
  });

  await stashSolvedToken();
  return { immune: sawImmunity };
}

/**
 * SUBMIT-TIME SAFETY NET. Ensure a usable, fresh CAPTCHA token exists immediately before the caller
 * POSTs. Fast path: if the inline puzzle was already solved and the token is still fresh, this is a
 * no-op (the stashed token rides the POST as the header). Otherwise (never solved, or the token
 * expired between solve and submit — WAF tokens are short-lived) it re-renders the puzzle and awaits
 * a fresh solve before returning. No dead-end, and no silent-`getToken` shortcut (that would 405).
 *
 * No-op when the live WAF is disabled (local/mock/test).
 *
 * Rejects if the SDK fails to load or the resident can't complete the re-challenge — the caller
 * shows an accessible error and lets them retry.
 */
export async function ensureCaptchaToken(container: HTMLElement): Promise<void> {
  if (!CAPTCHA_ENABLED) return; // no live WAF in front (local/mock/test) — submit directly.
  if (hasFreshCaptchaToken()) return; // inline solve is still fresh — POST straight through.
  await renderCaptchaInline(container);
}

/** Re-exported so tests/config consumers don't reach into config directly. */
export { CAPTCHA_ENABLED };
